import { useTranslation } from "react-i18next";
import { Button, Spinner, Modal, ModalBody, ModalFooter, ModalHeader } from "reactstrap";
import { useState } from "react";
import classNames from "classnames";
import { useNavigateWithQuery } from "src/Hooks/useNavigateWithQuery";
import TooltipWrapper from "src/components/Common/TooltipWrapper";
import isEmpty from "lodash/isEmpty";

const ButtonCollapse = ({ handleCollapse, isCollapsed }) => {
  return (
    <Button outline size="sm" className="px-1 btn-collapse height-by-px-36" onClick={handleCollapse}>
      <i className={`bi bi-chevron-${isCollapsed ? "down" : "up"} f-s-14 fw-bold text-dark`} />
    </Button>
  );
};

const SingleDashboard = ({ handleClick, isReady, isLoading, isShow = true, id }) => {
  const { t } = useTranslation("flight-simulator");
  return isShow ? (
    isLoading ? (
      <Spinner size="sm" color="secondary" className="mx-1" />
    ) : (
      <TooltipWrapper id={`dashboard-${id}`} tooltipText={t("list.table.dashboard")}>
        <button
          className="btn btn-blank px-1 border-0 py-0"
          onClick={() => handleClick()}
          onFocus={(e) => {
            // immediately blurs after focus
            e.target.blur();
          }}
        >
          <i className={`bi bi-speedometer2 ${isReady ? "text-success" : "text-dark"} f-s-20`} />
        </button>
      </TooltipWrapper>
    )
  ) : null;
};

const ButtonDownload = ({ url = "", id, handleDownload }) => {
  const { t } = useTranslation("flight-simulator");
  return !!url ? (
    <TooltipWrapper id={`download-${id}`} tooltipText={t("list.table.download")}>
      <a
        href={url}
        onClick={(e) => {
          e.stopPropagation();
          if (handleDownload) {
            e.preventDefault();
            handleDownload();
          }
        }}
        className="d-inline-block height-by-px-26"
      >
        <i className="bi bi-cloud-download f-s-20 text-dark" />
      </a>
    </TooltipWrapper>
  ) : null;
};

const ButtonCopy = ({ isShow = true, toUrl, id, target = "_self", prefix = "", noChangeCUrrentPath = false }) => {
  const { t } = useTranslation("flight-simulator");
  const navigateWithQuery = useNavigateWithQuery();
  return isShow ? (
    <TooltipWrapper id={`copy-${prefix}-${id}`} tooltipText={t("list.table.copy")}>
      <button
        onClick={() =>
          navigateWithQuery({
            newPath: toUrl,
            queryUpdates: { id },
            target,
            noChangeCUrrentPath,
          })
        }
        className="btn btn-blank p-0 border-0"
      >
        <i className="bi bi-copy f-s-20 text-dark" />
      </button>
    </TooltipWrapper>
  ) : null;
};

const ButtonRetry = ({ isShow = true, toUrl, id, target = "_self", prefix = "", noChangeCUrrentPath = false }) => {
  const { t } = useTranslation("flight-simulator");
  const navigateWithQuery = useNavigateWithQuery();
  return isShow ? (
    <TooltipWrapper id={`retry-${prefix}-${id}`} tooltipText={t("new.retry")}>
      <button
        onClick={(e) => {
          e.stopPropagation();
          navigateWithQuery({
            newPath: toUrl,
            queryUpdates: { id },
            target,
            noChangeCUrrentPath,
          });
        }}
        className="btn btn-blank p-0 border-0"
      >
        <i className="bi bi-arrow-clockwise f-s-20 text-dark" />
      </button>
    </TooltipWrapper>
  ) : null;
};

const ButtonFileViewer = ({ handleClick, id, className }) => {
  const { t } = useTranslation("flight-simulator");
  return (
    <TooltipWrapper id={`fileview-${id}`} tooltipText={t("list.table.display_data")}>
      <button
        className={classNames("btn btn-blank border-0 p-0", className)}
        onClick={() => handleClick()}
        onFocus={(e) => {
          // immediately blurs after focus
          e.target.blur();
        }}
      >
        <i className="bi bi-file-earmark-text f-s-20 text-dark"></i>
      </button>
    </TooltipWrapper>
  );
};

const ButtonInputView = ({ handleClick, id, className }) => {
  const { t } = useTranslation("flight-simulator");
  return (
    <TooltipWrapper id={`inputView-${id}`} tooltipText={t("list.table.display_data")}>
      <button className={classNames("btn btn-blank border-0 p-0", className)} onClick={() => handleClick()}>
        <i className="bi bi-file-earmark-text f-s-20 text-dark"></i>
      </button>
    </TooltipWrapper>
  );
};
const ButtonSimId = ({ handleClick, children = <></>, className }) => {
  return (
    <button onClick={() => handleClick()} className={classNames("bg-transparent p-0 border-0", className)}>
      {children}
    </button>
  );
};

const ButtonErrorWarning = ({ isShow = false, errorContent, id, iconClassName = "" }) => {
  return isShow ? (
    <TooltipWrapper id={`serie-item-${id}`} tooltipText={errorContent}>
      <i className={`bi text-danger bi-exclamation-triangle f-s-20 px-1 ${iconClassName}`} />
    </TooltipWrapper>
  ) : null;
};

const ButtonResultDisplay = ({ isShow, handleClick, id }) => {
  const { t } = useTranslation("gnc-simulator");
  return isShow ? (
    <TooltipWrapper id={`result-display-${id}`} tooltipText={t("list.table.display_data")}>
      <button
        className="btn btn-blank border-0 p-0"
        onClick={() => handleClick()}
        onFocus={(e) => {
          // immediately blurs after focus
          e.target.blur();
        }}
      >
        <i className="bi bi-ui-checks-grid f-s-20 text-dark"></i>
      </button>
    </TooltipWrapper>
  ) : null;
};

const ButtonTerminalSimulation = ({ isShow, handleSubmit, id }) => {
  const { t } = useTranslation("flight-simulator");
  const [toggle, setToggle] = useState(false);
  const [isSubmit, setIsSubmit] = useState(false);

  return isShow ? (
    <>
      <TooltipWrapper id={`terminal-button-${id}`} tooltipText={t("list.table.terminal")}>
        <button
          className="btn btn-blank border-0 p-0"
          onClick={(e) => {
            e.stopPropagation();
            setToggle(true);
          }}
          onFocus={(e) => {
            // immediately blurs after focus
            e.target.blur();
          }}
        >
          <i className="bi bi-stop-circle f-s-20 text-dark"></i>
        </button>
      </TooltipWrapper>

      <Modal isOpen={toggle} toggle={() => setToggle(false)} backdrop="static" size="md" centered>
        <ModalHeader>{t("list.table.terminal_popup_title")}</ModalHeader>
        <ModalBody>
          <div>{t("list.table.terminal_popup_content")}</div>
        </ModalBody>
        <ModalFooter>
          <button className="btn btn-secondary d-inline-block width-by-px-75" onClick={() => setToggle(false)}>
            {t("list.table.no")}
          </button>
          <button
            className="btn btn-danger d-inline-block width-by-px-75"
            onClick={() => {
              setIsSubmit(true);
              handleSubmit?.(id, () => {
                setIsSubmit(false);
                setToggle(false);
              });
            }}
            disabled={isSubmit}
          >
            {t("list.table.yes")}
          </button>
        </ModalFooter>
      </Modal>
    </>
  ) : null;
};

const ButtonReferenceHistory = ({ handleClick, id }) => {
  const { t } = useTranslation("gnc-simulator-6dof");
  return (
    <TooltipWrapper id={`reference-history-${id}`} tooltipText={t("list.table.reference_history.title")}>
      <button
        className="btn btn-blank border-0 p-0 reference-history-button"
        onClick={() => handleClick()}
        onFocus={(e) => {
          // immediately blurs after focus
          e.target.blur();
        }}
      >
        <i className="pi pi-history f-s-16 text-dark"></i>
      </button>
    </TooltipWrapper>
  );
};

const ButtonAssignTagsToResources = ({
  isShow = true,
  handleClick,
  id,
  translation = "common",
  tooltipText = "assign_tags_to_multi_resources",
  tags = [],
}) => {
  const { t } = useTranslation(translation);
  return isShow ? (
    <TooltipWrapper id={`${id}-multi-tags`} tooltipText={t(tooltipText)}>
      <button
        className={classNames("btn btn-blank px-1 border-0 py-0 height-by-px-21 d-flex align-items-center justify-content-center", {
          "opacity-50": isEmpty(tags),
        })}
        onClick={() => handleClick()}
        onFocus={(e) => {
          // immediately blurs after focus
          e.target.blur();
        }}
      >
        <i className="bi bi-tags f-s-16 text-dark" />
      </button>
    </TooltipWrapper>
  ) : null;
};

export {
  ButtonCollapse,
  SingleDashboard,
  ButtonDownload,
  ButtonCopy,
  ButtonRetry,
  ButtonFileViewer,
  ButtonErrorWarning,
  ButtonResultDisplay,
  ButtonTerminalSimulation,
  ButtonReferenceHistory,
  ButtonInputView,
  ButtonSimId,
  ButtonAssignTagsToResources,
};
